import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router";
import * as Icons from "lucide-react";
import { ChevronDown, Mail, Menu, Phone, X } from "lucide-react";
import { SITE_TAGLINE } from "@veo/types";
import { cn } from "@veo/ui";
import { BrandIcon, type BrandKey } from "./BrandIcons";
import { settingString, useSite, type NavNode } from "../lib/site";

/**
 * Header and footer.
 *
 * Both are driven entirely by the CMS: navigation comes from the HEADER,
 * FOOTER_PRIMARY, FOOTER_SECONDARY and LEGAL menus, and the contact details,
 * social links and risk disclaimer come from public settings. Nothing here is
 * a hard-coded link, so an editor can restructure the site without a deploy.
 */

function NavIcon({ name, className }: { name: string | null; className?: string }) {
  if (!name) return null;
  const Component = Icons[name as keyof typeof Icons] as typeof Icons.Circle | undefined;
  if (!Component) return null;
  return <Component aria-hidden className={className} />;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export function SiteHeader() {
  const { header, settings } = useSite();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const siteName = settingString(settings, "site.name", "VEO Consultancy");
  const tagline = settingString(settings, "site.tagline", SITE_TAGLINE);

  /**
   * Navigating closes both menus. Without this, following a mega-menu link
   * leaves the panel hanging open over the page it just loaded.
   *
   * Adjusted during render rather than in an effect: React re-renders
   * immediately with the corrected state, so the panel is never painted open
   * on the new page — an effect would show one frame of it first.
   */
  const [lastKey, setLastKey] = useState(location.key);
  if (lastKey !== location.key) {
    setLastKey(location.key);
    setDrawerOpen(false);
    setOpenMenuId(null);
  }

  // The drawer is a full-screen overlay, so the page behind it must not scroll.
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  // Escape closes whichever layer is open, and closes the mega menu without
  // losing keyboard position — focus returns to the trigger it came from.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (openMenuId) {
        const trigger = navRef.current?.querySelector<HTMLElement>(
          `[data-menu-id="${openMenuId}"]`,
        );
        setOpenMenuId(null);
        trigger?.focus();
      } else if (drawerOpen) {
        setDrawerOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openMenuId, drawerOpen]);

  // A pointer leaving the menu closes it after a short grace period, so the
  // diagonal travel from a trigger to the far corner of the panel is forgiving.
  const scheduleClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenuId(null), 180);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => () => cancelClose(), [cancelClose]);

  return (
    // Sits on the page ground rather than pure white, so the bar reads as part
    // of the surface it floats over instead of a lighter strip laid on top.
    <header className="sticky top-0 z-40 border-b border-surface-200 bg-surface-50/85 backdrop-blur-md">
      <div className="container-site flex h-16 items-center justify-between gap-6 lg:h-20">
        {/*
          The client's lockup, laid out for a horizontal bar.

          The supplied artwork is portrait — symbol over "VEO", "CONSULTANCY"
          and a tagline. Fitted whole into a 64px header its wordmark lands at
          about 15px and its tagline at 3px, so scripts/build-logo-assets.mjs
          sets the symbol and the wordmark side by side instead and drops the
          tagline, which has no legible size at this height. The portrait
          lockup, tagline and all, is what the footer and the admin sign-in use.

          We keep the horizontal brand mark as the primary logo here and add the
          tagline in text beneath it so the navigation can remain compact while
          the message remains visible. The link's aria-label carries the name for
          anyone who cannot see it, which is why the image itself is decorative.
        */}
        <Link
          to="/"
          className="flex flex-col items-start leading-none"
          aria-label={`${siteName} — home`}
        >
          <img
            src="/logo-veo-horizontal.png"
            alt=""
            width={551}
            height={176}
            className="h-10 w-auto shrink-0 lg:h-12"
          />
          <span className="mt-1 text-[10px] font-medium tracking-[0.12em] text-navy-700 lg:text-[11px]">
            {tagline}
          </span>
        </Link>

        <nav
          ref={navRef}
          aria-label="Main"
          className="hidden items-center gap-1 lg:flex"
          onMouseLeave={scheduleClose}
        >
          {header.items.map((item) =>
            item.children.length > 0 ? (
              <MegaMenu
                key={item.id}
                item={item}
                isOpen={openMenuId === item.id}
                onOpen={() => {
                  cancelClose();
                  setOpenMenuId(item.id);
                }}
                onClose={() => setOpenMenuId(null)}
                onPointerLeave={scheduleClose}
                onPointerEnter={cancelClose}
              />
            ) : (
              <NavLink
                key={item.id}
                to={item.href}
                {...(item.opensInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className={({ isActive }) =>
                  cn(
                    "rounded px-3 py-2 text-sm transition-colors",
                    isActive ? "font-semibold text-navy-900" : "text-ink-600 hover:text-navy-900",
                  )
                }
              >
                {item.label}
              </NavLink>
            ),
          )}

          <Link
            to="/book-consultation"
            className="ml-3 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-semibold text-cream-50 transition-colors hover:bg-navy-800"
          >
            Book a consultation
          </Link>
        </nav>

        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-expanded={drawerOpen}
          aria-controls="mobile-nav"
          className="-mr-1.5 rounded p-1.5 text-navy-900 lg:hidden"
        >
          <Menu aria-hidden className="size-6" />
          <span className="sr-only">Open menu</span>
        </button>
      </div>

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} items={header.items} />
    </header>
  );
}

/**
 * One top-level item with a dropdown panel.
 *
 * The trigger is a button rather than a link. A link that also opens a panel
 * has two conflicting activations, and a keyboard user gets whichever the
 * implementation happened to bind — a button with `aria-expanded` says exactly
 * what it does. The section landing page appears as the first link inside.
 */
function MegaMenu({
  item,
  isOpen,
  onOpen,
  onClose,
  onPointerEnter,
  onPointerLeave,
}: {
  item: NavNode;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}) {
  const panelId = useId();

  return (
    <div className="relative" onMouseEnter={onPointerEnter} onMouseLeave={onPointerLeave}>
      <button
        type="button"
        data-menu-id={item.id}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => (isOpen ? onClose() : onOpen())}
        onFocus={onOpen}
        className={cn(
          "flex items-center gap-1.5 rounded px-3 py-2 text-sm transition-colors",
          isOpen ? "text-navy-900" : "text-ink-600 hover:text-navy-900",
        )}
      >
        {item.label}
        <ChevronDown
          aria-hidden
          className={cn("size-4 transition-transform", isOpen && "rotate-180")}
        />
      </button>

      {isOpen && (
        <div
          id={panelId}
          className="absolute left-1/2 top-full z-50 w-[min(42rem,90vw)] -translate-x-1/2 pt-3"
        >
          <div className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-xl">
            <ul className="grid gap-1 p-3 sm:grid-cols-2">
              {item.children.map((child) => (
                <li key={child.id}>
                  <Link
                    to={child.href}
                    {...(child.opensInNewTab
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="flex gap-3 rounded-lg p-3 transition-colors hover:bg-cream-200"
                  >
                    {child.icon && (
                      <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-navy-900 text-gold-300">
                        <NavIcon name={child.icon} className="size-4" />
                      </span>
                    )}
                    <span>
                      <span className="block text-sm font-semibold text-navy-900">
                        {child.label}
                      </span>
                      {child.description && (
                        <span className="mt-1 block text-xs leading-relaxed text-ink-500">
                          {child.description}
                        </span>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="border-t border-ink-100 bg-cream-100 px-6 py-3">
              <Link
                to={item.href}
                className="text-sm font-semibold text-navy-700 hover:text-navy-900"
              >
                All {item.label.toLowerCase()}
                <span aria-hidden> →</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The mobile navigation drawer.
 *
 * Focus is moved into the panel on open and returned to the trigger on close,
 * and the rest of the page is `inert` while it is open so a swipe or a Tab
 * cannot land behind the overlay.
 */
function MobileDrawer({
  open,
  onClose,
  items,
}: {
  open: boolean;
  onClose: () => void;
  items: NavNode[];
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  if (!open) return null;

  // Portalled to <body>: the drawer sits inside the header, whose
  // `backdrop-blur` makes it the containing block for fixed-position
  // descendants — so without the portal this "fullscreen" overlay is confined
  // to the header's 64px strip and the menu items land invisibly outside it.
  // Rendering only happens after a click, so `document` is always defined.
  return createPortal(
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-navy-900/50"
      />

      <div
        id="mobile-nav"
        role="dialog"
        aria-modal="true"
        aria-label="Main navigation"
        className="absolute inset-y-0 right-0 flex w-[min(22rem,88vw)] flex-col bg-white shadow-2xl"
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-ink-200 px-5">
          <span className="font-display text-lg text-navy-900">Menu</span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="-mr-1.5 rounded p-1.5 text-navy-900"
          >
            <X aria-hidden className="size-6" />
            <span className="sr-only">Close menu</span>
          </button>
        </div>

        <nav aria-label="Main" className="flex-1 overflow-y-auto px-5 py-4">
          <ul className="space-y-1">
            {items.map((item) => (
              <li key={item.id}>
                {item.children.length > 0 ? (
                  // Native disclosure again — it is keyboard-operable and
                  // screen-reader-correct without a line of state.
                  <details className="group border-b border-ink-100 py-1">
                    <summary className="flex cursor-pointer list-none items-center justify-between py-3 text-base font-medium text-navy-900 marker:content-['']">
                      {item.label}
                      <ChevronDown
                        aria-hidden
                        className="size-5 text-gold-600 transition-transform group-open:rotate-180"
                      />
                    </summary>
                    <ul className="space-y-1 pb-2 pl-1">
                      <li>
                        <Link
                          to={item.href}
                          className="block py-2 text-sm font-semibold text-navy-700"
                        >
                          All {item.label.toLowerCase()}
                        </Link>
                      </li>
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <Link to={child.href} className="block py-2 text-sm text-ink-600">
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : (
                  <Link
                    to={item.href}
                    {...(item.opensInNewTab
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="block border-b border-ink-100 py-4 text-base font-medium text-navy-900"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          <Link
            to="/book-consultation"
            className="mt-6 block rounded-md bg-navy-900 px-4 py-3.5 text-center text-sm font-semibold text-cream-50"
          >
            Book a consultation
          </Link>
        </nav>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

const SOCIAL_KEYS: { key: string; label: string; icon: BrandKey }[] = [
  { key: "social.linkedin", label: "LinkedIn", icon: "linkedin" },
  { key: "social.twitter", label: "X", icon: "x" },
  { key: "social.facebook", label: "Facebook", icon: "facebook" },
  { key: "social.instagram", label: "Instagram", icon: "instagram" },
  { key: "social.youtube", label: "YouTube", icon: "youtube" },
];

/**
 * An address, with a break opportunity marked at the `@`.
 *
 * `wrap-anywhere` on the link is what stops the address widening its column,
 * but on its own it breaks wherever the line happens to run out — mid-domain,
 * which reads as a typo rather than a wrap. A `<wbr>` offers the algorithm a
 * real break opportunity, and a real one is always preferred to an emergency
 * one, so the address splits at the `@` whenever it splits at all.
 *
 * `<wbr>` is a zero-width element, not a character: copying the address still
 * yields the address, and the `mailto:` is built from the unsplit string.
 */
function EmailText({ email }: { email: string }) {
  const at = email.lastIndexOf("@");
  if (at === -1) return <>{email}</>;

  return (
    <>
      {email.slice(0, at + 1)}
      <wbr />
      {email.slice(at + 1)}
    </>
  );
}

export function SiteFooter() {
  const { footerPrimary, footerSecondary, legal, settings } = useSite();

  const siteName = settingString(settings, "site.name", "VEO Consultancy");
  const description = settingString(settings, "site.description");
  const email = settingString(settings, "contact.email");
  const phone = settingString(settings, "contact.phone");
  const copyrightHolder = settingString(settings, "legal.copyrightHolder", siteName);
  const registration = settingString(settings, "legal.companyRegistration");
  const address = [
    settingString(settings, "contact.addressLine1"),
    settingString(settings, "contact.addressLine2"),
    settingString(settings, "contact.city"),
    settingString(settings, "contact.country"),
  ].filter(Boolean);

  const socials = SOCIAL_KEYS.map((social) => ({
    ...social,
    href: settingString(settings, social.key),
  })).filter((social) => social.href);

  const columns = [footerPrimary, footerSecondary].filter((menu) => menu.items.length > 0);

  /**
   * How many blocks there are to lay out, which is what the track count
   * follows. Each of these can be absent — a site with no description, no
   * contact details or one footer menu should get even columns rather than
   * even columns and a hole where a block did not render.
   */
  const hasAbout = Boolean(description) || socials.length > 0;
  const hasContact = Boolean(phone || email || address.length > 0);
  const blockCount = 1 + (hasAbout ? 1 : 0) + (hasContact ? 1 : 0) + columns.length;

  return (
    <footer className="bg-navy-900 textured-dark text-navy-200">
      <div className="container-site py-14 lg:py-16">
        {/*
          Five even columns: the mark, who we are, the two menus, and how to
          reach us. No double-width brand block beside thinner ones.

          Contact sits last rather than beside the description. It is the block
          a reader goes looking for, and the far right is where a footer is
          scanned to — the menus are a list to run an eye down on the way.

          The brand block used to be one column carrying the logo, the
          description and the socials, with contact split out beside it. The
          logo now stands on its own at the far left — a mark reads as a mark
          when it is not competing with a paragraph for the same track — and
          the description takes the column next to it.

          The track count follows the number of blocks actually rendered, so a
          site with no description or only one footer menu gets even columns
          rather than even columns and a hole.
        */}
        {/*
          `[&>*]:min-w-0` is the guard, not a tweak.

          A grid track's automatic minimum is its content's minimum, so one
          long unbreakable string in any column — an address, a URL, a menu
          label an editor pastes in — widens that track past its share and
          pushes the whole grid off the right of the page. Everything in this
          footer comes from the CMS, so that string is always one edit away.
          Zeroing the minimum lets each column take its share and wrap inside
          it, which is the behaviour the layout already assumes.
        */}
        <div
          className={cn(
            "grid gap-10 [&>*]:min-w-0",
            FOOTER_GRID[blockCount] ?? "lg:grid-cols-4",
          )}
        >
          <div>
            {/*
              The full lockup, in cream so it sits on the navy ground. The
              artwork ships on an opaque white background; the cream variant is
              keyed out of it by scripts/build-logo-assets.mjs, because a white
              rectangle here would be obvious.

              The name and tagline are inside the artwork, so they are not
              repeated as text — an image of "VEO CONSULTANCY" above the words
              "VEO Consultancy" reads as a mistake. The accessible name on the
              image carries them for anyone who cannot see it.

              Sized to the tagline rather than to the column. The tagline band
              is about 5% of the artwork's height, so at the 80px this used to
              be it drew at four pixels — present, unreadable, and the reason
              the block looked like a small mark rather than the full lockup.
              At 144px it lands near eight, which is the whole point of using
              the portrait artwork in the one place there is room for it.
            */}
            <img
              src="/logo-veo-light.png"
              alt={siteName}
              width={377}
              height={480}
              className="h-28 w-auto lg:h-36"
            />
          </div>

          {hasAbout && (
            <div>
              {description && <p className="text-sm leading-relaxed">{description}</p>}

              {/*
                The social links sit with the description rather than under the
                logo. They are how the firm is reached rather than part of the
                mark, and the logo column is the one place on the page where
                the lockup stands alone.
              */}
              {socials.length > 0 && (
                <ul className={cn("flex flex-wrap gap-3", description && "mt-6")}>
                  {socials.map((social) => (
                    <li key={social.key}>
                      <a
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex size-9 items-center justify-center rounded-md border border-navy-600 transition-colors hover:border-gold-500 hover:text-gold-300"
                      >
                        <BrandIcon name={social.icon} className="size-4" />
                        <span className="sr-only">{social.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {columns.map((menu) => (
            <FooterColumn key={menu.location} menu={menu} />
          ))}

          {hasContact && (
            <div>
              {/* Same heading treatment as FooterColumn, so this heading
                  and the two menu headings to its left read as one row. */}
              <h2 className="text-xs font-semibold uppercase tracking-wider text-cream-100">
                Get in touch
              </h2>

              <ul className="mt-3 space-y-2 text-sm">
                {phone && (
                  <li className="flex items-start gap-2.5">
                    <Phone aria-hidden className="mt-0.5 size-4 shrink-0 text-gold-400" />
                    {/* tel: makes the number one tap on the device most visitors
                        are actually holding. */}
                    <a
                      href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                      className="hover:text-cream-100"
                    >
                      {phone}
                    </a>
                  </li>
                )}
                {email && (
                  <li className="flex min-w-0 items-start gap-2.5">
                    <Mail aria-hidden className="mt-0.5 size-4 shrink-0 text-gold-400" />
                    {/*
                      `wrap-anywhere`, not `break-words`.

                      An address is one unbreakable token, and the two
                      properties differ on exactly the case that matters here:
                      `break-word` permits a mid-word break once the box is
                      already narrow, but still reports the whole address as
                      this column's minimum width. `anywhere` lowers that
                      minimum too, which is what lets the track shrink to its
                      share instead of forcing the grid wider than the page.
                    */}
                    <a href={`mailto:${email}`} className="wrap-anywhere hover:text-cream-100">
                      <EmailText email={email} />
                    </a>
                  </li>
                )}
              </ul>

              {address.length > 0 && (
                <address className="mt-4 text-sm not-italic leading-relaxed">
                  {address.join(", ")}
                </address>
              )}
            </div>
          )}
        </div>

        {/*
          No risk disclaimer here.

          It is still published — the closing section of the homepage, every
          service page, and the Risk Disclaimer page itself all carry it, and
          the `legal.riskDisclaimer` setting still feeds those. Removing it
          from the footer only stops it repeating on every page.
        */}

        <div className="mt-12 flex flex-col gap-4 border-t border-navy-700 pt-6 text-xs text-navy-400 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {copyrightHolder}. All rights reserved.
            {registration && <span className="ml-2">Registration no. {registration}.</span>}
          </p>

          {legal.items.length > 0 && (
            <nav aria-label="Legal">
              <ul className="flex flex-wrap gap-x-5 gap-y-2">
                {legal.items.map((item) => (
                  <li key={item.id}>
                    <Link to={item.href} className="hover:text-cream-100">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      </div>
    </footer>
  );
}

/**
 * Footer track counts, keyed by how many blocks there are to lay out.
 *
 * Written as whole class names rather than composed at runtime: Tailwind reads
 * source text, so `lg:grid-cols-${n}` would compile to nothing.
 */
const FOOTER_GRID: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
  5: "sm:grid-cols-2 lg:grid-cols-5",
};

const MENU_LABELS: Record<string, string> = {
  FOOTER_PRIMARY: "Footer",
  FOOTER_SECONDARY: "More",
};

/**
 * A footer column.
 *
 * Top-level items become headings with their children beneath; an item with no
 * children is simply a link. That means an editor arranging the footer menu in
 * the CMS gets the layout they see in the tree, without a separate concept of
 * "group" to learn.
 */
function FooterColumn({ menu }: { menu: { location: string; items: NavNode[] } }) {
  return (
    <nav aria-label={MENU_LABELS[menu.location] ?? "Footer"}>
      {menu.items.map((item) => (
        <div key={item.id} className="mb-8 last:mb-0">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-cream-100">
            {item.children.length > 0 ? (
              item.label
            ) : (
              <Link to={item.href} className="hover:text-gold-300">
                {item.label}
              </Link>
            )}
          </h2>
          {item.children.length > 0 && (
            <ul className="mt-3 space-y-2 text-sm">
              {item.children.map((child) => (
                <li key={child.id}>
                  <Link
                    to={child.href}
                    {...(child.opensInNewTab
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="hover:text-cream-100"
                  >
                    {child.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </nav>
  );
}
